import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Form, Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button } from 'primereact/button';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import AddReportText from './AddReportText';
import { BEHAVIOR_SECTION } from './constants';
import SaveContentConfirmationDialog from './components/SaveContentConfirmationDialog';

const i18nMessages = defineMessages({
  textType: {
    id: 'menu.item-bank-management.text-type',
    defaultMessage: 'Text Type',
  },
  level: {
    id: 'menu.item-bank-management.level',
    defaultMessage: 'Level',
  },
  sequence: {
    id: 'menu.item-bank-management.sequence',
    defaultMessage: 'Sequence',
  },
  textId: {
    id: 'menu.item-bank-management.textId',
    defaultMessage: 'Text Id',
  },
  text: {
    id: 'menu.item-bank-management.text',
    defaultMessage: 'Text',
  },
  actions: {
    id: 'menu.item-bank-management.actions',
    defaultMessage: 'Actions',
  },
});

const AddReportTexts = ({
  availableReportTextTypes,
  availableTextLevels,
  availableSequences,
  reportTextValues,
  processFormValues,
}) => {
  const [index, setIndex] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const { formatMessage } = useIntl();
  const history = useHistory();
  const { values: parentFormValues, setFieldValue, isSubmitting } = useFormikContext();

  const { section } = parentFormValues;

  const reportTextsValues = parentFormValues.reportTexts.filter((item) => item.sectionCode === section);

  const initialValues =
    index != null
      ? {
          sectionCode: reportTextsValues[index].sectionCode,
          code: reportTextsValues[index].code,
          text: reportTextsValues[index].text,
          type: reportTextsValues[index].type,
          level: reportTextsValues[index].level,
          sequence: reportTextsValues[index].sequence,
        }
      : {
          sectionCode: BEHAVIOR_SECTION,
          code: '',
          text: '',
          type: reportTextValues.type,
          level: reportTextValues.level,
          sequence: reportTextValues.sequence,
        };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(),
    text: Yup.string().required(),
    type: Yup.string().required(),
    level: Yup.string().required(),
    sequence: Yup.string().required(),
  });

  const actionsBody = (data, { rowIndex }) => (
    <div className="align-center">
      <Button type="button" className="p-button-secondary" icon="pi pi-pencil" onClick={() => setIndex(rowIndex)} />
    </div>
  );

  return (
    <>
      <SaveContentConfirmationDialog
        showConfirmationDialog={showConfirmationDialog}
        setShowConfirmationDialog={setShowConfirmationDialog}
        isSubmitting={isSubmitting}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikBag) => {
          formikBag.resetForm();

          if (index != null) {
            const updatedValues = reportTextsValues.map((reportText, reportTextIndex) => {
              if (reportTextIndex === index) {
                return { ...values };
              }

              return reportText;
            });

            setFieldValue('reportTexts', updatedValues);
            setIndex(null);
          } else {
            setFieldValue('reportTexts', [...parentFormValues.reportTexts, { ...values }]);

            processFormValues(values);
          }
        }}
      >
        <Form>
          <AddReportText
            section={section}
            availableReportTextTypes={availableReportTextTypes}
            availableTextLevels={availableTextLevels}
            availableSequences={availableSequences}
          />
          <FormattedMessage id="item-bank-management.context-type.save-report-text" defaultMessage="Save Report Text">
            {(text) => <Button type="submit" className="p-button-secondary inner-form-submit-button" label={text} />}
          </FormattedMessage>
        </Form>
      </Formik>
      <DataTable value={reportTextsValues} className="text-center">
        <Column field="type" header={formatMessage(i18nMessages.textType)} />
        <Column field="code" header={formatMessage(i18nMessages.textId)} />
        <Column field="level" header={formatMessage(i18nMessages.level)} />
        <Column field="sequence" header={formatMessage(i18nMessages.sequence)} />
        <Column field="text" header={formatMessage(i18nMessages.text)} />
        <Column body={actionsBody} header={formatMessage(i18nMessages.actions)} />
      </DataTable>
      <div className="outer-form-submit-button">
        <FormattedMessage id="actions.save" defaultMessage="Save">
          {(text) => <Button type="button" label={text} onClick={() => setShowConfirmationDialog(true)} />}
        </FormattedMessage>
        <FormattedMessage id="actions.back" defaultMessage="Back">
          {(text) => (
            <Button type="button" onClick={() => history.goBack()} className="p-button-secondary" label={text} />
          )}
        </FormattedMessage>
      </div>
    </>
  );
};

AddReportTexts.propTypes = {
  availableReportTextTypes: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableTextLevels: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableSequences: PropTypes.arrayOf(PropTypes.any).isRequired,
  reportTextValues: PropTypes.objectOf(PropTypes.any).isRequired,
  processFormValues: PropTypes.func.isRequired,
};

export default AddReportTexts;
